import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import MenuLayout from "./MenuLayout";
import "../styles/AnalysisPage.css";
import { getAccountChallengeDetail, getBalanceEquityHistory, getTrades, getAccountAnalysis } from "../api/challenge";
import { getAccountCompetitionRanks } from "../api/competition";

const formatMoney = (val) => {
  if (val === null || val === undefined || val === "") return "-";
  const num = Number(val);
  if (Number.isNaN(num)) return "-";
  return num.toLocaleString("en-US", { maximumFractionDigits: 2 });
};

const formatPercent = (val) => {
  if (val === null || val === undefined || val === "") return "-";
  const num = Number(val);
  if (Number.isNaN(num)) return "-";
  return `${num}%`;
};

const formatNumber = (val) => {
  if (val === null || val === undefined || val === "") return "-";
  const num = Number(val);
  if (Number.isNaN(num)) return "-";
  return num.toLocaleString("en-US", { maximumFractionDigits: 2 });
};

const formatDate = (val) => {
  if (!val) return "-";
  const d = new Date(val);
  if (Number.isNaN(d.getTime())) return val;
  return d.toLocaleDateString("fa-IR");
};

const formatDateTime = (val) => {
  if (!val) return "-";
  const d = new Date(val);
  if (Number.isNaN(d.getTime())) return val;
  return d.toLocaleString("fa-IR");
};

const clampPercent = (val) => {
  const num = Number(val);
  if (Number.isNaN(num)) return 0;
  return Math.min(Math.max(num, 0), 100);
};

const tradeFilterOptions = [
  { key: "today", label: "امروز" },
  { key: "this_week", label: "این هفته" },
  { key: "successful", label: "موفق" },
  { key: "unsuccessful", label: "ناموفق" },
  { key: "under_30_seconds", label: "کمتر از 30 ثانیه" },
  { key: "under_1_minute", label: "کمتر از 1 دقیقه" },
  { key: "buy", label: "خرید" },
  { key: "sell", label: "فروش" },
];





const AnalysisPage = () => {
  const { accountNumber } = useParams();
  const [selectedFilter, setSelectedFilter] = useState("today");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [detail, setDetail] = useState(null);
  const [history, setHistory] = useState([]);
  const [trades, setTrades] = useState([]);
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [tradesLoading, setTradesLoading] = useState(false);
  const [tradesError, setTradesError] = useState("");
  const [analysisError, setAnalysisError] = useState("");
  const [competitionRanks, setCompetitionRanks] = useState([]);

  useEffect(() => {
    if (!accountNumber) {
      setError("شماره چالش انتخاب نشده است.");
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      setError("");
      try {
        const d = await getAccountChallengeDetail(accountNumber);
        setDetail(d);
      } catch (err) {
        setError(err?.message || "مشکل در دریافت اطلاعات حساب");
        setDetail(null);
      }

      try {
        const h = await getBalanceEquityHistory(accountNumber);
        setHistory(Array.isArray(h) ? h : []);
      } catch {
        setHistory([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();

    const fetchCompetitionRanks = () => {
      getAccountCompetitionRanks(accountNumber)
        .then((res) => setCompetitionRanks(Array.isArray(res?.competitions) ? res.competitions : []))
        .catch(() => setCompetitionRanks([]));
    };
    fetchCompetitionRanks();

    const detailTimer = window.setInterval(async () => {
      try {
        const d = await getAccountChallengeDetail(accountNumber);
        setDetail(d);
        setError("");
      } catch {
        // Keep the last valid live values during a temporary network failure.
      }
    }, 5000);

    const historyTimer = window.setInterval(async () => {
      try {
        const h = await getBalanceEquityHistory(accountNumber);
        setHistory(Array.isArray(h) ? h : []);
      } catch {
        // A missing history must not interrupt the live account cards.
      }
    }, 30000);
    const competitionRankTimer = window.setInterval(fetchCompetitionRanks, 15000);

    return () => {
      window.clearInterval(detailTimer);
      window.clearInterval(historyTimer);
      window.clearInterval(competitionRankTimer);
    };
  }, [accountNumber]);

  const fetchTrades = async (opts = {}) => {
    if (!accountNumber) return;
    setTradesLoading(true);
    setTradesError("");
    try {
      const params = {};
      const activeFilter = opts.filter ?? selectedFilter;
      const useCustomDates = activeFilter === "custom";
      if (activeFilter && activeFilter !== "custom") {
        params.filter = activeFilter;
      }
      const fromDate = opts.startDate ?? startDate;
      const toDate = opts.endDate ?? endDate;
      if (useCustomDates && (!fromDate || !toDate)) {
        setTradesLoading(false);
        return;
      }
      if (useCustomDates && fromDate && toDate) {
        params.start_date = fromDate;
        params.end_date = toDate;
      }
      const res = await getTrades(accountNumber, params);
      setTrades(Array.isArray(res?.results) ? res.results : []);
    } catch (err) {
      setTrades([]);
      setTradesError(err?.message || "خطا در دریافت معاملات");
    } finally {
      setTradesLoading(false);
    }
  };

  useEffect(() => {
    fetchTrades();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountNumber, selectedFilter]);

  useEffect(() => {
    if (!accountNumber) return;
    const run = async () => {
      setAnalysisError("");
      try {
        const res = await getAccountAnalysis(accountNumber);
        setAnalysis(res || null);
      } catch (err) {
        setAnalysis(null);
        setAnalysisError(err?.message || "??? ?? ????? ???? (?????? ???)?? ????");
      }
    };
    run();
  }, [accountNumber]);

  const handleApplyDate = () => {
    if (!startDate || !endDate) {
      setTradesError("تاریخ شروع و پایان را وارد کنید.");
      return;
    }
    setSelectedFilter("custom");
    fetchTrades({ filter: "custom", startDate, endDate });
  };

  const statusTone =
    detail?.challenge_status === "failed"
      ? "danger"
      : detail?.challenge_status === "passed"
      ? "success"
      : "info";
  const statusText = detail?.status_description || "-";

  const metrics = useMemo(
    () => [
      { key: "pnl", title: "سود / زیان خالص", value: detail?.profit ?? null, tone: "red", arrow: detail?.profit >= 0 ? "up" : "down" },
      { key: "balance", title: "بالانس حساب", value: detail?.balance ?? null, tone: "yellow", arrow: "up" },
      { key: "drawdownTotal", title: "دراودان کل", value: detail?.total_drawdown ?? null, tone: "green", arrow: "down" },
      { key: "drawdownDaily", title: "دراودان روزانه", value: detail?.daily_drawdown ?? null, tone: "blue", arrow: "down" },
      { key: "drawdownOpen", title: `افت معاملات باز (حد ${detail?.max_open_trade_loss ?? "-"}٪)`, value: detail?.open_trade_drawdown ?? null, tone: "red", arrow: "down" },
    ],
    [detail]
  );

  const historyPoints = useMemo(() => {
    return (history || []).map((row) => {
      const balance = Number(row.balance ?? 0);
      const equity = Number(row.equity ?? 0);
      const dailyDrawdownLimit = Number(row.daily_drawdown_limit ?? 0);
      const totalDrawdownLimit = Number(row.total_drawdown_limit ?? 0);
      return {
        label: formatDate(row.date),
        balance: Number.isNaN(balance) ? 0 : balance,
        equity: Number.isNaN(equity) ? 0 : equity,
        dailyDrawdownLimit: Number.isNaN(dailyDrawdownLimit) ? 0 : dailyDrawdownLimit,
        totalDrawdownLimit: Number.isNaN(totalDrawdownLimit) ? 0 : totalDrawdownLimit,
      };
    });
  }, [history]);

  const lineMetrics = useMemo(() => {
    if (!historyPoints.length) return { min: 0, max: 0, range: 1, paddedMin: 0, paddedMax: 1 };
    const vals = historyPoints.flatMap((p) => [p.balance, p.equity, p.dailyDrawdownLimit, p.totalDrawdownLimit]);
    const max = Math.max(...vals);
    const min = Math.min(...vals);
    const rawRange = max - min;
    const pad = rawRange === 0 ? Math.max(Math.abs(max) * 0.1, 1) : rawRange * 0.1;
    const paddedMin = min - pad;
    const paddedMax = max + pad;
    const range = paddedMax - paddedMin || 1;
    return { min, max, range, paddedMin, paddedMax };
  }, [historyPoints]);

  const yTicks = useMemo(() => {
    if (!historyPoints.length) return [];
    const steps = 4;
    const { paddedMin, paddedMax } = lineMetrics;
    const stepVal = (paddedMax - paddedMin) / steps;
    return new Array(steps + 1).fill(0).map((_, idx) => paddedMax - stepVal * idx);
  }, [historyPoints, lineMetrics]);

  const buildPath = (key) => {
    if (!historyPoints.length) return "";
    const width = 640;
    const height = 220;
    const step = historyPoints.length > 1 ? width / (historyPoints.length - 1) : 0;
    const { paddedMin, range } = lineMetrics;
    return historyPoints
      .map((p, idx) => {
        const x = step * idx;
        const y = height - ((p[key] - paddedMin) / range) * height;
        return `${idx === 0 ? "M" : "L"}${x},${y}`;
      })
      .join(" ");
  };

  const formatAxisNumber = (val) => {
    const num = Number(val);
    if (Number.isNaN(num)) return "-";
    return num.toLocaleString("en-US", { maximumFractionDigits: 2 });
  };

  const dailyRiskUsage = clampPercent(detail?.daily_drawdown_limit_percent);
  const totalRiskUsage = clampPercent(detail?.total_drawdown_limit_percent);

  const analysisStats = useMemo(() => {
    const d = analysis || {};
    return [
      { label: "تعداد کل معاملات", value: formatNumber(d.total_trades) },
      { label: "معاملات موفق", value: formatNumber(d.successful_trades) },
      { label: "معاملات ناموفق", value: formatNumber(d.failed_trades) },
      { label: "میانگین سود", value: formatMoney(d.avg_profit) },
      { label: "میانگین ضرر", value: formatMoney(d.avg_loss) },
      { label: "حجم معاملات", value: formatNumber(d.trade_volume) },
      { label: "درصد برد", value: formatPercent(d.win_rate) },
      { label: "ضریب واریز", value: formatPercent(d.deposit_factor) },
    ];
  }, [analysis]);

  const renderArrow = (dir) => (
    <span className="kpi-arrow">
      {dir === "up" ? (
        <svg width="29" height="19" viewBox="0 0 29 19" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M27.5 1.5L18.0003 11C17.8297 11.1706 17.7434 11.2561 17.667 11.3234C16.4331 12.4109 14.5672 12.4109 13.3333 11.3234C13.2569 11.2561 13.17 11.1707 12.9993 11C12.8286 10.8293 12.7432 10.7439 12.6668 10.6766C11.4329 9.58908 9.56622 9.58908 8.33229 10.6766C8.25609 10.7437 8.17096 10.8289 8.00108 10.9987L1.5 17.5M27.5 1.5L27.4993 11.1M27.5 1.5H17.7495"
            stroke="white"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ) : (
        <svg width="29" height="19" viewBox="0 0 29 19" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M1.5 17.5L10.9997 8C11.1703 7.82938 11.2566 7.74389 11.333 7.67658C12.5669 6.58911 14.4328 6.58908 15.6667 7.67656C15.7431 7.74389 15.83 7.8293 16.0007 8.00002C16.1714 8.17072 16.2568 8.25612 16.3332 8.32344C17.5671 9.41092 19.4338 9.41092 20.6677 8.32344C20.7439 8.25628 20.829 8.17115 20.9989 8.00127L27.5 1.5M1.5 17.5L1.50073 7.9M1.5 17.5H11.2505"
            stroke="white"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )}
    </span>
  );

  return (
    <MenuLayout>
      <div className="analysis-page">
        <header className="analysis-header">
          <h1 className="analysis-title">تحلیل حساب</h1>
          <p className="analysis-subtitle">مرور خلاصه عملکرد حساب، وضعیت چالش و تغییرات موجودی.</p>
        </header>

        {(loading || error) && (
          <div className="analysis-notices">
            {loading && (
              <div className="analysis-notice info">
                <span className="notice-text">در حال دریافت اطلاعات...</span>
              </div>
            )}
            {error && !loading && (
              <div className="analysis-notice danger">
                <span className="notice-text">{error}</span>
              </div>
            )}
          </div>
        )}

        {!loading && detail && (
          <>
            {statusTone !== "info" && (
              <div className="analysis-notices">
                <div className={`analysis-notice ${statusTone}`}>
                  <span className="notice-text">{statusText}</span>
                </div>
              </div>
            )}

            <div className="analysis-info-grid">
              {[
                { label: "شماره حساب :", value: detail?.account_number || "-" },
                { label: "وضعیت :", value: detail?.challenge_status || "-" },
                { label: "تاریخ شروع :", value: formatDate(detail?.start_date) },
                { label: "پایان چالش :", value: formatDate(detail?.end_date) },
                { label: "توضیحات :", value: detail?.status_description || "-" },
              ].map((item) => (
                <div className="info-card" key={item.label}>
                  <span className="info-label">{item.label}</span>
                  <span className="info-value">{item.value}</span>
                </div>
              ))}
            </div>

            {competitionRanks.length > 0 && (
              <section className="analysis-competition" aria-label="رتبه حساب در مسابقات">
                <div className="analysis-competition__intro">
                  <span className="analysis-competition__eyebrow">مسابقات فعال</span>
                  <h2>جایگاه این حساب در رقابت</h2>
                  <p>رتبه از مجموع سود تمام حساب‌های چالش و Real شما در بازه مسابقه محاسبه می‌شود.</p>
                </div>
                <div className="analysis-competition__ranks">
                  {competitionRanks.map((item) => (
                    <article className="analysis-competition__rank" key={item.competition_id}>
                      <div>
                        <span>{item.competition_type === "weekly" ? "هفتگی" : "ماهانه"}</span>
                        <strong>{item.competition_name}</strong>
                      </div>
                      <div className="analysis-competition__position">
                        <small>رتبه</small>
                        <b>{formatNumber(item.rank)}</b>
                      </div>
                      <div className="analysis-competition__profit">
                        <small>سود تجمیعی</small>
                        <b>{formatMoney(item.profit)} $</b>
                      </div>
                    </article>
                  ))}
                </div>
              </section>
            )}

            <div className="analysis-kpi-grid">
              {metrics.map((m) => {
                const positive = (m.value ?? 0) >= 0;
                const toneClass = `kpi-card ${m.tone}`;
                const valueClass =
                  m.key === "pnl"
                    ? positive
                      ? "green-text"
                      : "red-text"
                    : positive
                    ? `${m.tone}-text`
                    : "red-text";
                return (
                  <div className={toneClass} key={m.key}>
                    <div className="kpi-icon">{renderArrow(m.arrow)}</div>
                    <div className="kpi-title">{m.title}</div>
                    <div className={`kpi-value ${valueClass}`}>{formatMoney(m.value)}</div>
                  </div>
                );
              })}
            </div>

            <div className="analysis-banner">
              <span className="banner-text">مانده فعلی حساب</span>
              <span className="banner-value">{formatMoney(detail?.balance)}</span>
            </div>

            <div className="analysis-dual">
              <div className="ring-card">
                <div className="ring-wrapper">
                  <div
                    className="ring-outer"
                    style={{
                      background: `conic-gradient(#d6d600 0deg ${totalRiskUsage * 3.6}deg, #f2f2f2 ${totalRiskUsage * 3.6}deg 360deg)`,
                    }}
                  >
                    <div className="ring-label-top">سقف ریسک</div>
                    <div
                      className="ring-inner-arc"
                      style={{
                        background: `conic-gradient(#ffa500 0deg ${dailyRiskUsage * 3.6}deg, #f2f2f2 ${dailyRiskUsage * 3.6}deg 360deg)`,
                      }}
                    >
                      <div className="ring-label-center">روزانه / کل</div>
                    </div>
                  </div>
                </div>
                <div className="ring-stats">
                  <div className="ring-stat">
                    <span className="dot yellow"></span>
                    <span className="ring-stat-label">ریسک کل :</span>
                    <span className="ring-stat-value yellow-text">
                      {formatPercent(totalRiskUsage)} | {formatMoney(detail?.total_drawdown)} از {formatMoney(detail?.max_total_loss_amount)}
                    </span>
                  </div>
                  <div className="ring-divider"></div>
                  <div className="ring-stat">
                    <span className="dot orange"></span>
                    <span className="ring-stat-label">ریسک روزانه :</span>
                    <span className="ring-stat-value orange-text">
                      {formatPercent(dailyRiskUsage)} | {formatMoney(detail?.daily_drawdown)} از {formatMoney(detail?.max_daily_loss_amount)}
                    </span>
                  </div>
                </div>
              </div>
              <div className="mini-progress">
                <div className="mini-row green">
                  <span className="mini-label">پیشرفت سود</span>
                  <div className="mini-bar">
                    <div
                      className="mini-fill green"
                      style={{ width: `${clampPercent(detail?.profit_target_percent)}%` }}
                    ></div>
                  </div>
                  <span className="mini-value">{formatPercent(detail?.profit_target_percent)}</span>
                </div>
                <div className="mini-row orange">
                  <span className="mini-label">روزهای غیرفعال</span>
                  <div className="mini-bar">
                    <div
                      className="mini-fill orange"
                      style={{ width: `${clampPercent(detail?.inactive_days_percent)}%` }}
                    ></div>
                  </div>
                  <span className="mini-value">{formatNumber(detail?.inactive_days)} از {formatNumber(detail?.inactivity_limit_days)}</span>
                </div>
              </div>
            </div>

            <div className="analysis-chart-card">
              <div className="chart-title">
                <span>نمودار بالانس و اکیوتی</span>
                <div className="chart-legend">
                  <span className="dot yellow"></span>
                  <span>بالانس</span>
                  <span className="dot blue"></span>
                  <span>اکیوتی</span>
                  <span className="dot orange"></span>
                  <span>حد دراودان روزانه</span>
                  <span className="dot red"></span>
                  <span>حد دراودان کل</span>
                </div>
              </div>
              {historyPoints.length === 0 ? (
                <div className="analysis-notice info">
                  <span className="notice-text">داده‌ای برای نمایش نمودار موجود نیست.</span>
                </div>
              ) : (
                <div className="line-chart-wrapper">
                  <div className="line-chart-shell">
                    <div className="chart-y-labels">
                      {yTicks.map((t, idx) => (
                        <span key={idx}>{formatAxisNumber(t)}</span>
                      ))}
                    </div>
                    <svg className="line-chart" viewBox="0 0 640 240" preserveAspectRatio="none">
                      <g transform="translate(0,10)">
                        {yTicks.map((t, idx) => {
                          const y = 220 - ((t - lineMetrics.paddedMin) / lineMetrics.range) * 220;
                          return <line key={idx} x1="0" x2="640" y1={y} y2={y} className="chart-grid-line" />;
                        })}
                        <line x1="0" x2="640" y1="220" y2="220" className="chart-axis" />
                        <path className="line-path balance" d={buildPath("balance")} />
                        <path className="line-path equity" d={buildPath("equity")} />
                        <path className="line-path daily-drawdown" d={buildPath("dailyDrawdownLimit")} />
                        <path className="line-path total-drawdown" d={buildPath("totalDrawdownLimit")} />
                      </g>
                    </svg>
                  </div>
                  <div className="chart-x-labels">
                    {historyPoints.map((p, idx) => (
                      <span key={idx}>{p.label}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            
            
            <div className="analysis-table-card">
              <div className="table-filters">
                {tradeFilterOptions.map((opt) => (
                  <button
                    key={opt.key}
                    className={`filter-btn ${selectedFilter === opt.key ? "active" : ""}`}
                    onClick={() => {
                      setTradesError("");
                      setSelectedFilter(opt.key);
                    }}
                  >
                    {opt.label}
                  </button>
                ))}
                <div className="date-filters">
                  <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
                  <span>تا</span>
                  <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
                  <button className="apply-btn" onClick={handleApplyDate}>
                    اعمال
                  </button>
                </div>
              </div>

              
              <div className="orders-table-head">
                <span>زمان شروع</span>
                <span>زمان پایان</span>
                <span>جفت ارز</span>
                <span>نوع</span>
                <span>نتیجه</span>
                <span>حجم (لات)</span>
                <span>قیمت باز</span>
                <span>قیمت بسته</span>
                <span>سود / زیان</span>
              </div>
              <div className="orders-table-body">
                {tradesLoading && (
                  <div className="analysis-notice info">
                    <span className="notice-text">در حال دریافت معاملات...</span>
                  </div>
                )}
                {tradesError && !tradesLoading && (
                  <div className="analysis-notice danger">
                    <span className="notice-text">{tradesError}</span>
                  </div>
                )}
                {!tradesLoading && !tradesError && trades.length === 0 && (
                  <div className="analysis-notice info">
                    <span className="notice-text">معامله‌ای یافت نشد.</span>
                  </div>
                )}
                {!tradesLoading && !tradesError &&
                  trades.map((row) => {
                    const resultClass = row.trade_result === "won" ? "success-text" : row.trade_result === "lost" ? "red-text" : "";
                    const pnlNum = Number(row.profit_loss ?? 0);
                    const pnlClass = pnlNum >= 0 ? "success-text" : "red-text";
                    const typeLabel = row.trade_type === "buy" ? "خرید" : row.trade_type === "sell" ? "فروش" : "-";
                    const resultLabel = row.trade_result === "won" ? "برد" : row.trade_result === "lost" ? "باخت" : row.trade_result || "-";
                    return (
                      <div className="orders-table-row" key={row.id}>
                        <span>{formatDateTime(row.start_time)}</span>
                        <span>{formatDateTime(row.end_time)}</span>
                        <span>{row.trade_symbol || "-"}</span>
                        <span>{typeLabel}</span>
                        <span className={resultClass}>{resultLabel}</span>
                        <span>{formatMoney(row.lot_size)}</span>
                        <span>{formatMoney(row.open_price)}</span>
                        <span>{formatMoney(row.close_price)}</span>
                        <span className={pnlClass}>{formatMoney(row.profit_loss)}</span>
                      </div>
                    );
                  })}
              </div>
            </div>

            <div className="analysis-bottom">
              <div className="analysis-bottom-main">
                <div className="analysis-bottom-title">تحلیل حساب (پیشرفت کلی)</div>
                {analysisError && (
                  <div className="analysis-notice danger">
                    <span className="notice-text">{analysisError}</span>
                  </div>
                )}
                <div className="analysis-bottom-grid">
                  {analysisStats.map((item, idx) => (
                    <div className="bottom-card" key={idx}>
                      <span className="bottom-card-percent">{item.value}</span>
                      <span className="bottom-card-label">{item.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </MenuLayout>
  );
};

export default AnalysisPage;
